import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { capturePasswordRecoveryFromLocation } from '../utils/passwordRecovery';

// Get environment variables (trim – undvik tomma strängar från felaktig CI/.env)
const supabaseUrl = String(import.meta.env.VITE_SUPABASE_URL ?? '').trim();
const supabaseAnonKey = String(import.meta.env.VITE_SUPABASE_ANON_KEY ?? '').trim();

// 🚨 SAFETY GUARD – prevent PROD build from pointing to DEV Supabase project
if (
  import.meta.env.PROD &&
  supabaseUrl?.includes("zqkshqcrebmnsjlgyoyh")
) {
  throw new Error("🚨 PROD build is pointing to DEV database");
}

// Lazy initialization to avoid circular dependencies
let supabaseInstance: SupabaseClient | null = null;

function getSupabaseClient(): SupabaseClient {
  if (!supabaseInstance) {
    // Fail fast if env vars are missing (prevents confusing runtime errors)
    if (!supabaseUrl || !supabaseAnonKey) {
      throw new Error(
        'Supabase environment variables are missing. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.'
      );
    }

    if (typeof window !== 'undefined') {
      capturePasswordRecoveryFromLocation();
    }

    supabaseInstance = createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        // Keep existing key so users are not force-logged-out by another key migration.
        storage: typeof window !== 'undefined' ? window.localStorage : undefined,
        storageKey: 'sb-auth-token',
      },
    });

    // Mobile Safari suspends timers while backgrounded. Restart auto-refresh on resume
    // instead of calling refreshSession() (that raced with autoRefresh and wiped sessions).
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const syncAutoRefresh = () => {
        if (document.visibilityState === 'visible') {
          void supabaseInstance!.auth.startAutoRefresh();
        } else {
          void supabaseInstance!.auth.stopAutoRefresh();
        }
      };
      document.addEventListener('visibilitychange', syncAutoRefresh);
      syncAutoRefresh();
    }
  }
  return supabaseInstance;
}

// Create a single supabase client for interacting with your database
export const supabase = new Proxy({} as SupabaseClient, {
  get(target, prop) {
    const client = getSupabaseClient();
    const value = (client as any)[prop];
    if (typeof value === 'function') {
      return value.bind(client);
    }
    return value;
  }
});

// Export a function to check if Supabase is properly configured
export const isSupabaseConfigured = () => {
  return Boolean(supabaseUrl && supabaseAnonKey);
};

// Export a function to test the Supabase connection
export const testSupabaseConnection = async () => {
  if (!isSupabaseConfigured()) {
    throw new Error('Supabase är inte konfigurerad. Kontrollera dina miljövariabler.');
  }

  try {
    const { error } = await supabase.from('products').select('id').limit(1);
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Failed to connect to Supabase:', error);
    return false;
  }
};
